/**
 * Confirmation contract for the dedicated "Delete account" page.
 *
 * To proceed with the (irreversible-after-grace-period) deletion the user must
 * re-type the EXACT email of the account being deleted into a confirmation box.
 * This is a deliberate friction step against ACCIDENTAL deletion and against
 * confusing "Delete account" with "Sign out". It is NOT a security boundary —
 * the real authorization is the session cookie the backend checks on
 * `requestAccountDeletion`; this only gates the destructive button in the UI.
 *
 * Matching is normalized with trim + lowercase (case-insensitive):
 *   - Email domains are case-insensitive by spec, and every mainstream provider
 *     also treats the local-part case-insensitively, so requiring exact case
 *     would only produce false negatives that frustrate a legitimate user.
 *   - A leading/trailing space from autofill or copy-paste should not block a
 *     correct confirmation.
 * The trade-off (someone could type a different-cased variant) is irrelevant:
 * this is an anti-mistake gate, not authentication.
 */
export function normalizeEmailForConfirmation(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Returns `true` only when `input` matches the account email under the
 * normalization rules above. An empty/absent account email never matches, so a
 * missing session can never accidentally enable the destructive button.
 */
export function emailMatchesAccount(
  input: string,
  accountEmail: string | null | undefined,
): boolean {
  if (!accountEmail) {
    return false;
  }
  const normalizedAccount = normalizeEmailForConfirmation(accountEmail);
  if (normalizedAccount === "") {
    return false;
  }
  return normalizeEmailForConfirmation(input) === normalizedAccount;
}

/** Suffix of the dedicated deletion route (the per-tab `/u/<n>` prefix varies). */
const DELETE_ACCOUNT_PATH_SUFFIX = "/profile/delete-account";

/** `true` when `pathname` still points at the dedicated deletion page. */
export function isDeleteAccountPath(pathname: string): boolean {
  return pathname.endsWith(DELETE_ACCOUNT_PATH_SUFFIX);
}

/**
 * Guard for the "a guest has no account to delete" redirect.
 *
 * A guest must be bounced off the deletion page to the profile — but the
 * redirect has to be issued AT MOST ONCE, and this predicate is what makes that
 * true. It is not a stylistic guard: without the `pathname` term the redirect
 * effect LOCKS UP THE WHOLE TAB.
 *
 * Why: `useSession().isVisitor()` is reactive on `activePathname()` (the per-tab
 * account is parsed out of the `/u/<n>` URL prefix). So an effect shaped like
 * «if visitor → navigate» reads the pathname and then CHANGES the pathname:
 * the navigation re-triggers the very effect that issued it. `isVisitor()` is
 * still `true` at the destination (a guest stays a guest), so the effect
 * navigates again, and again — a self-feeding synchronous Solid update loop that
 * never yields. Measured symptom: the renderer main thread spins inside
 * `runUpdates → runQueue → runTop`, the `load` event NEVER fires, in-flight
 * requests never settle and the page stays BLANK forever (not a slow page — a
 * hung one). It is the guest path, so nobody is signed in to notice, and the
 * only way out of the deletion page is closing the tab.
 *
 * Anchoring the redirect to «we are STILL on the deletion page» closes the loop
 * at its source: once the first navigation lands on `/profile`, the condition is
 * false and no second navigation is ever issued.
 *
 * `isPending` is honoured first so an in-flight session verification never
 * expels a real signed-in user mid-boot.
 */
export function shouldRedirectGuestAwayFromDeletion(input: {
  isPending: boolean;
  isVisitor: boolean;
  pathname: string;
}): boolean {
  if (input.isPending) {
    return false;
  }
  if (!input.isVisitor) {
    return false;
  }
  return isDeleteAccountPath(input.pathname);
}
